import type { IDBPDatabase } from 'idb';
import {
  type BurfyDocument,
  DocumentConflictError,
  DocumentNotFoundError,
  type DocumentStore,
  type DocumentSummary,
  createDocument,
  loadDocument,
  parseDocument,
} from '@burfy/docs-core';
import {
  type BurfyDocsDB,
  DEFAULT_DB_NAME,
  DOCUMENTS_STORE as STORE,
  openBurfyDocsDb,
} from './database.ts';

export { DEFAULT_DB_NAME };

/**
 * IndexedDB-backed document storage.
 *
 * v1 keeps everything in the browser: no server, no account. The interface is
 * `DocumentStore`, so replacing this with an HTTP implementation later touches
 * nothing above it — including the `rev` conflict check, which is exercised
 * here by two tabs long before it has to work across two devices.
 */

/**
 * Read the fields the list view needs, without trusting the record.
 *
 * Returns `null` for anything malformed. Deliberately *not* a full validation:
 * one corrupt record must not make the entire document list unopenable. The
 * strict check still happens in `get`, where the user is asking for that
 * specific document and an error is something they can act on.
 */
function toSummary(raw: unknown): DocumentSummary | null {
  if (typeof raw !== 'object' || raw === null) return null;

  const record = raw as Partial<BurfyDocument>;
  const meta = record.meta;
  if (typeof record.id !== 'string' || typeof meta !== 'object' || meta === null) {
    return null;
  }
  if (typeof meta.title !== 'string' || typeof meta.updatedAt !== 'string') return null;

  return {
    id: record.id,
    title: meta.title,
    createdAt: typeof meta.createdAt === 'string' ? meta.createdAt : meta.updatedAt,
    updatedAt: meta.updatedAt,
    rev: typeof record.rev === 'number' ? record.rev : 0,
  };
}

export interface IndexedDbDocumentStoreOptions {
  /**
   * Clock, injectable so tests are deterministic.
   *
   * Injected rather than faked globally: fake timers stall IndexedDB's own
   * internals, so a test that mocks `Date` ends up hanging on transactions
   * that never settle.
   */
  now?: () => string;
  /** Overridable so tests can isolate themselves in their own database. */
  databaseName?: string;
}

export class IndexedDbDocumentStore implements DocumentStore {
  #db: Promise<IDBPDatabase<BurfyDocsDB>> | null = null;
  readonly #now: () => string;
  readonly #databaseName: string;

  constructor(options: IndexedDbDocumentStoreOptions = {}) {
    this.#now = options.now ?? (() => new Date().toISOString());
    this.#databaseName = options.databaseName ?? DEFAULT_DB_NAME;
  }

  /** Which IndexedDB database this store is bound to. */
  get databaseName(): string {
    return this.#databaseName;
  }

  #connection(): Promise<IDBPDatabase<BurfyDocsDB>> {
    this.#db ??= openBurfyDocsDb(this.#databaseName);
    return this.#db;
  }

  /**
   * Release the underlying connection.
   *
   * Not part of `DocumentStore` — it is specific to a connection-holding
   * implementation. It matters because an open IndexedDB connection blocks
   * `deleteDB` and blocks version upgrades in other tabs indefinitely.
   */
  async close(): Promise<void> {
    const pending = this.#db;
    this.#db = null;
    if (pending) (await pending).close();
  }

  /**
   * Every stored record, exactly as written, with no validation.
   *
   * Not part of `DocumentStore` — it is specific to this implementation, like
   * `close()`. The asset sweep needs it: `get` refuses a document it cannot
   * render and `list` skips one it cannot summarise, and a document in either
   * category still owns its images. Sweeping on a view that omitted them would
   * delete the pictures of the documents least able to survive it.
   */
  async listRawRecords(): Promise<unknown[]> {
    const db = await this.#connection();
    return db.getAll(STORE);
  }

  async list(): Promise<DocumentSummary[]> {
    const db = await this.#connection();
    const records = await db.getAll(STORE);

    const summaries: DocumentSummary[] = [];
    for (const record of records) {
      const summary = toSummary(record);
      if (summary) summaries.push(summary);
      else console.warn('[burfy-docs] skipping unreadable document record', record);
    }

    // Most recently edited first — what a document list is for.
    return summaries.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async get(id: string): Promise<BurfyDocument | null> {
    const db = await this.#connection();
    const raw = await db.get(STORE, id);
    if (raw === undefined) return null;

    // Migrate forward, then validate. Never hand the editor a document we
    // have not checked — it would render partially and autosave the loss back.
    return loadDocument(raw);
  }

  async create(init: { title?: string } = {}): Promise<BurfyDocument> {
    const db = await this.#connection();
    const document = createDocument({
      now: this.#now(),
      ...(init.title === undefined ? {} : { title: init.title }),
    });
    await db.put(STORE, document);
    return document;
  }

  async save(document: BurfyDocument): Promise<BurfyDocument> {
    /**
     * Never write what cannot be read back.
     *
     * `get` refuses a document it cannot render, and until now `save` would
     * happily store one — so content that broke the editor was written over
     * the last good copy and the document became permanently unopenable. The
     * path there is short: a crash unmounts the editor, `useAutosave` flushes
     * on unmount, and the flush commits the very content that crashed it.
     *
     * Validating here costs a parse per save, which is debounced and off the
     * typing path. A document that cannot be reopened costs everything.
     */
    parseDocument(document);

    const db = await this.#connection();

    // Read and write in one transaction: without it, two tabs can both read
    // rev 4 and both write rev 5, and the second silently discards the first.
    const tx = db.transaction(STORE, 'readwrite');
    const existing = await tx.store.get(document.id);

    /**
     * Abort, and absorb the rejection `tx.done` raises as a result.
     *
     * We are about to throw a specific, actionable error; leaving the
     * transaction's own `AbortError` unhandled would surface as a spurious
     * unhandled promise rejection and drown the real one in error reporting.
     */
    const abort = () => {
      tx.abort();
      void tx.done.catch(() => undefined);
    };

    if (existing === undefined) {
      abort();
      throw new DocumentNotFoundError(document.id);
    }
    if (existing.rev !== document.rev) {
      abort();
      throw new DocumentConflictError(document.id, document.rev, existing.rev);
    }

    const next: BurfyDocument = {
      ...document,
      rev: document.rev + 1,
      meta: { ...document.meta, updatedAt: this.#now() },
    };

    await tx.store.put(next);
    await tx.done;
    return next;
  }

  async remove(id: string): Promise<void> {
    const db = await this.#connection();
    await db.delete(STORE, id);
  }
}
